import React, { useCallback, useState } from 'react';
import { View, Text, TextInput, ActivityIndicator } from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import {
  TrendingUp,
  Upload,
  FileText,
  Sparkles,
  Users,
  CheckCircle,
  AlertTriangle,
  RotateCcw,
  Clock,
  X,
} from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { AppModal, ModalAction } from '@/components/modal';
import * as DocumentPicker from 'expo-document-picker';
import { reportError } from '@/lib/errors';
import { useAnalyzePitchDeck } from '@/lib/investor';
import { useQueryClient } from '@tanstack/react-query';
import { deckAnalysesQueryKey } from '@/lib/investor/use-deck-analyses';
import type { AnalyzePitchDeckResult } from '@/lib/investor/pitch-deck-analysis';
import { UsageIndicator } from '@/components/subscription/UsageIndicator';
import { hapticSuccess, hapticError } from '@/lib/haptics';
import { useRouter } from 'expo-router';

interface PitchDeckAnalysisModalProps {
  visible: boolean;
  onClose: () => void;
  /**
   * The server refused with 403 'Consent required' — a race, or consent
   * withdrawn on another device (the founder should always have consented
   * BEFORE this modal opens, via the owning screen's gate). AppModals must
   * never stack, so this modal closes ITSELF and hands control back to the
   * owning screen, which reopens the consent sheet there.
   */
  onConsentRequired?: () => void;
}

// Get user-friendly error message
function getErrorMessage(error: string | null | undefined): { title: string; message: string; canRetry: boolean } {
  const errorStr = error?.toLowerCase() || '';
  reportError(error, { context: 'PitchDeckAnalysisModal.getErrorMessage', silent: true });

  if (errorStr.includes('network') || errorStr.includes('fetch') || errorStr.includes('timeout')) {
    return {
      title: 'Connection Error',
      message: 'Unable to reach the server. Please check your internet connection and try again.',
      canRetry: true,
    };
  }
  if (errorStr.includes('url-based analysis not yet supported')) {
    return {
      title: 'URL Analysis Not Available',
      message: 'URL-based analysis is not yet supported. Please upload a PDF file instead.',
      canRetry: false,
    };
  }
  if (errorStr.includes('too large') || errorStr.includes('size') || errorStr.includes('25mb')) {
    return {
      title: 'File Too Large',
      message: 'The file exceeds the 25MB limit. Please compress or split the document.',
      canRetry: false,
    };
  }
  if (errorStr.includes('auth') || errorStr.includes('token') || errorStr.includes('session') || errorStr.includes('expired')) {
    return {
      title: 'Session Expired',
      message: 'Your session has expired. Please log in again to continue.',
      canRetry: false,
    };
  }
  // 'consent' is handled BEFORE this function ever runs — see
  // `handleStartAnalysis`, which detects it on the raw result and reopens the
  // consent sheet instead of rendering an error card. This branch is now
  // unreachable in practice; kept only so an unexpected consent-shaped
  // message from elsewhere still reads honestly rather than falling through
  // to the generic "Analysis Failed" catch-all.
  if (errorStr.includes('consent')) {
    return {
      title: 'Consent Required',
      message: 'AI-processing consent is required before this analysis can run.',
      canRetry: false,
    };
  }
  if (errorStr.includes('format') || errorStr.includes('corrupt') || errorStr.includes('invalid file')) {
    return {
      title: 'Invalid File Format',
      message: 'The file could not be processed. Please ensure it\'s a valid PDF or PowerPoint file.',
      canRetry: false,
    };
  }
  if (errorStr.includes('quota') || errorStr.includes('rate limit')) {
    return {
      title: 'Service Busy',
      message: 'Our AI service is experiencing high demand. Please try again in a few moments.',
      canRetry: true,
    };
  }
  return {
    title: 'Analysis Failed',
    message: error || 'Something went wrong while analyzing the pitch deck. Please try again.',
    canRetry: true,
  };
}

function getStageLabel(stg: string): string {
  switch (stg) {
    case 'uploading': return 'Uploading pitch deck...';
    case 'extracting': return 'Extracting data from pitch deck...';
    case 'checking': return 'Checking for existing company...';
    case 'creating': return 'Creating startup profile...';
    case 'scoring': return 'Calculating readiness score...';
    case 'complete': return 'Analysis complete!';
    case 'duplicate': return 'Company already scored \u2014 redirecting...';
    case 'error': return 'Analysis failed';
    case 'cancelled': return 'Analysis cancelled';
    case 'timeout': return 'Still analysing on the server\u2026';
    default: return 'Preparing...';
  }
}

export function PitchDeckAnalysisModal({ visible, onClose, onConsentRequired }: PitchDeckAnalysisModalProps) {
  const colors = useThemeColors();
  const router = useRouter();

  const [uploadedFile, setUploadedFile] = useState<{ name: string; uri: string; size?: number } | null>(null);
  const [newAnalysisUrl, setNewAnalysisUrl] = useState('');
  // Uses the hook's own result type rather than re-declaring the shape — the
  // local copy had already drifted from it.
  const [analysisResult, setAnalysisResult] = useState<AnalyzePitchDeckResult | null>(null);

  const { analyze, isAnalyzing, stage, progress, error: analysisError, cancel: cancelAnalysis, reset: resetAnalysis } = useAnalyzePitchDeck();
  const queryClient = useQueryClient();

  // A client-side timeout is NOT an error: the edge function keeps running and
  // commits its work. Showing "Analysis Failed" for it told investors an
  // analysis had failed when the company had in fact been created, scored and
  // added to the pipeline — and invited a retry of work already succeeding.
  const isStillProcessing = stage === 'timeout' || !!analysisResult?.stillProcessing;
  const hasError =
    !isStillProcessing && (stage === 'error' || analysisError || analysisResult?.error);
  const errorInfo = hasError ? getErrorMessage(analysisError || analysisResult?.error) : null;

  const handlePickDocument = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: [
          'application/pdf',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        ],
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets?.length) {
        const asset = result.assets[0];
        setUploadedFile({
          name: asset.name,
          uri: asset.uri,
          size: asset.size,
        });
        setNewAnalysisUrl('');
      }
    } catch (error) {
      reportError(error, { context: 'PitchDeckAnalysisModal.handleDocumentPick', level: 'warning' });
    }
  }, []);

  const handleStartAnalysis = useCallback(async () => {
    if (!uploadedFile && !newAnalysisUrl.trim()) return;

    setAnalysisResult(null);

    const result = await analyze({
      fileUri: uploadedFile?.uri,
      fileName: uploadedFile?.name,
      url: newAnalysisUrl.trim() || undefined,
    });

    // A race (or consent withdrawn on another device) — the owning screen's
    // gate already checked consent before this modal opened, so the server
    // saying no now means the local state was stale. Close THIS modal (never
    // stack a second AppModal on top of it) and hand off to the owning
    // screen, which reopens the consent sheet there.
    if (!result.success && result.error === 'Consent required') {
      handleDismiss();
      onConsentRequired?.();
      return;
    }

    setAnalysisResult(result);

    if (result.success) {
      hapticSuccess();

      // Nowhere to navigate to yet — and that is the point. The analysis was
      // ACCEPTED, not finished: the company does not exist until extraction
      // resolves an identity server-side. Pushing to /startup-detail here (as
      // this did) would land on an id that has not been minted.
      //
      // Close and send them to the pipeline, where the card tracks the work
      // through Analysing → Scoring and fills in on its own. They are free to
      // go and do something else.
      // Make the card appear immediately rather than on the next poll —
      // otherwise the pipeline looks unchanged for up to 5s after the upload,
      // which is the exact ambiguity this feature removes.
      queryClient.invalidateQueries({ queryKey: deckAnalysesQueryKey });

      setTimeout(() => {
        handleDismiss();
        router.push('/(investor)/dealflow');
      }, 1200);
    } else {
      hapticError();
    }
  }, [uploadedFile, newAnalysisUrl, analyze, router, onConsentRequired]);

  const handleDismiss = useCallback(() => {
    if (isAnalyzing) cancelAnalysis();
    onClose();
    setUploadedFile(null);
    setNewAnalysisUrl('');
    setAnalysisResult(null);
    resetAnalysis();
  }, [onClose, isAnalyzing, cancelAnalysis, resetAnalysis]);

  const canStart = Boolean(uploadedFile || newAnalysisUrl.trim());
  const isRestart = stage === 'cancelled' || stage === 'error';

  const footer = (
    <AppModal.Actions>
      {isAnalyzing ? (
        // Live progress (spinner + stage + %) is shown in the body; the footer
        // just offers Cancel. No redundant disabled "Analyzing…" button.
        <ModalAction
          label="Cancel"
          variant="destructive"
          icon={<ActivityIndicator size="small" color={colors.error} />}
          onPress={() => {
            cancelAnalysis();
            setAnalysisResult(null);
          }}
          accessibilityLabel="Cancel pitch deck analysis"
        />
      ) : (
        <>
          <ModalAction
            label={analysisResult?.success ? 'Close' : 'Cancel'}
            variant="neutral"
            onPress={handleDismiss}
            accessibilityLabel={analysisResult?.success ? 'Close analysis dialog' : 'Close'}
          />
          {!analysisResult?.success && (
            <ModalAction
              label={isRestart ? 'Restart' : 'Start Analysis'}
              variant="primary"
              disabled={!canStart}
              icon={isRestart
                ? <RotateCcw size={18} color={canStart ? 'white' : colors.text.tertiary} />
                : <Sparkles size={18} color={canStart ? 'white' : colors.text.tertiary} />}
              onPress={() => {
                if (isRestart) {
                  resetAnalysis();
                  setAnalysisResult(null);
                }
                handleStartAnalysis();
              }}
              accessibilityLabel={isRestart ? 'Restart pitch deck analysis' : 'Start pitch deck analysis'}
            />
          )}
        </>
      )}
    </AppModal.Actions>
  );

  return (
    <AppModal
      visible={visible}
      onClose={handleDismiss}
      title="Analyze New Startup"
      maxWidth={520}
      footer={footer}
    >
      <View className="flex-row items-center gap-3 mb-6">
        <View
          className="w-14 h-14 rounded-2xl items-center justify-center"
          style={{ backgroundColor: colors.primary[100] }}
        >
          <Sparkles size={28} color={colors.primary[600]} />
        </View>
        <View className="flex-1">
          <Text style={{ color: colors.text.secondary }} className="text-sm">
            Submit a pitch deck for AI-powered analysis
          </Text>
          <UsageIndicator feature="basic_scoring" suffix="this month" />
        </View>
      </View>

      {/* Upload Section */}
              <View className="mb-4">
                <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-2">
                  Upload Pitch Deck
                </Text>

                {uploadedFile ? (
                  <View
                    className="rounded-xl p-4 flex-row items-center gap-3"
                    style={{ backgroundColor: colors.traffic.greenLight, borderWidth: 1, borderColor: colors.success }}
                  >
                    <View
                      className="w-12 h-12 rounded-lg items-center justify-center"
                      style={{ backgroundColor: colors.surface.primary }}
                    >
                      <FileText size={24} color={colors.success} />
                    </View>
                    <View className="flex-1">
                      <Text style={{ color: colors.text.primary }} className="font-medium" numberOfLines={1}>
                        {uploadedFile.name}
                      </Text>
                      {uploadedFile.size && (
                        <Text style={{ color: colors.text.tertiary }} className="text-xs">
                          {(uploadedFile.size / (1024 * 1024)).toFixed(1)} MB
                        </Text>
                      )}
                    </View>
                    <View className="flex-row items-center gap-2">
                      <CheckCircle size={20} color={colors.success} />
                      <AnimatedPressable
                        onPress={() => setUploadedFile(null)}
                        accessibilityRole="button"
                        accessibilityLabel="Remove uploaded file"
                        className="w-8 h-8 rounded-full items-center justify-center"
                        style={{ backgroundColor: colors.surface.primary }}
                      >
                        <X size={16} color={colors.text.tertiary} />
                      </AnimatedPressable>
                    </View>
                  </View>
                ) : (
                  <AnimatedPressable
                    onPress={handlePickDocument}
                    accessibilityRole="button"
                    accessibilityLabel="Upload pitch deck as PDF or PowerPoint, max 25 megabytes"
                    className="rounded-xl p-6 items-center"
                    style={{
                      backgroundColor: colors.surface.secondary,
                      borderWidth: 2,
                      borderColor: colors.primary[300],
                      borderStyle: 'dashed',
                    }}
                  >
                    <Upload size={32} color={colors.primary[600]} />
                    <Text style={{ color: colors.text.primary }} className="font-medium mt-3">
                      Tap to select file
                    </Text>
                    <Text style={{ color: colors.text.tertiary }} className="text-sm mt-1">
                      PDF or PPTX (max 25MB)
                    </Text>
                  </AnimatedPressable>
                )}
              </View>

              {/* Or URL Section */}
              {!uploadedFile && (
                <>
                  <View className="flex-row items-center gap-3 my-4">
                    <View className="flex-1 h-px" style={{ backgroundColor: colors.border.light }} />
                    <Text style={{ color: colors.text.tertiary }} className="text-sm">or paste a URL</Text>
                    <View className="flex-1 h-px" style={{ backgroundColor: colors.border.light }} />
                  </View>

                  <View
                    className="flex-row items-center rounded-xl overflow-hidden mb-4"
                    style={{ backgroundColor: colors.surface.secondary, borderWidth: 1, borderColor: colors.border.light }}
                  >
                    <View className="px-4 py-4">
                      <FileText size={20} color={colors.text.tertiary} />
                    </View>
                    <TextInput
                      value={newAnalysisUrl}
                      onChangeText={setNewAnalysisUrl}
                      placeholder="Google Drive, Dropbox, DocSend..."
                      placeholderTextColor={colors.text.tertiary}
                      accessibilityLabel="Pitch deck URL"
                      className="flex-1 py-4 pr-4"
                      style={{ color: colors.text.primary }}
                      editable={!isAnalyzing}
                    />
                  </View>
                </>
              )}

              {/* Progress Section */}
              {isAnalyzing && (
                <View
                  className="rounded-xl p-4 mb-6"
                  style={{ backgroundColor: colors.primary[50], borderWidth: 1, borderColor: colors.primary[200] }}
                >
                  {/* Stage indicator icons */}
                  <View className="flex-row items-center justify-between mb-4">
                    {[
                      { key: 'uploading', icon: Upload, label: 'Upload' },
                      { key: 'extracting', icon: FileText, label: 'Extract' },
                      { key: 'creating', icon: Users, label: 'Profile' },
                      { key: 'scoring', icon: TrendingUp, label: 'Score' },
                    ].map((item, idx, arr) => {
                      const Icon = item.icon;
                      const stageOrder = ['uploading', 'extracting', 'creating', 'scoring'];
                      const currentIdx = stageOrder.indexOf(stage);
                      const itemIdx = stageOrder.indexOf(item.key);
                      const isComplete = itemIdx < currentIdx;
                      const isCurrent = item.key === stage;

                      return (
                        <View key={item.key} className="flex-row items-center flex-1">
                          <View className="items-center flex-1">
                            <View
                              className="w-10 h-10 rounded-full items-center justify-center mb-1"
                              style={{
                                backgroundColor: isComplete || isCurrent ? colors.primary[600] : colors.primary[100],
                              }}
                            >
                              {isComplete ? (
                                <CheckCircle size={20} color="white" />
                              ) : (
                                <Icon size={18} color={isCurrent ? 'white' : colors.primary[400]} />
                              )}
                            </View>
                            <Text
                              className="text-xs font-medium"
                              style={{ color: isComplete || isCurrent ? colors.primary[700] : colors.primary[400] }}
                            >
                              {item.label}
                            </Text>
                          </View>
                          {idx < arr.length - 1 && (
                            <View
                              className="h-0.5 flex-1 mx-1"
                              style={{
                                backgroundColor: itemIdx < currentIdx ? colors.primary[600] : colors.primary[200],
                                marginTop: -16,
                              }}
                            />
                          )}
                        </View>
                      );
                    })}
                  </View>

                  <View className="flex-row items-center gap-3 mb-3">
                    <ActivityIndicator size="small" color={colors.primary[600]} />
                    <Text style={{ color: colors.primary[700] }} className="font-medium flex-1">
                      {getStageLabel(stage)}
                    </Text>
                    <Text style={{ color: colors.primary[600] }} className="font-bold">
                      {progress}%
                    </Text>
                  </View>
                  {/* Progress bar */}
                  <View
                    className="h-2 rounded-full overflow-hidden"
                    style={{ backgroundColor: colors.primary[100] }}
                  >
                    <View
                      className="h-full rounded-full"
                      style={{
                        backgroundColor: colors.primary[600],
                        width: `${progress}%`,
                      }}
                    />
                  </View>
                </View>
              )}

              {/* Accepted — not finished. Say so honestly. */}
              {analysisResult?.success && (
                <View
                  className="rounded-xl p-4 mb-6"
                  style={{
                    backgroundColor: colors.traffic.greenLight,
                    borderWidth: 1,
                    borderColor: colors.success,
                  }}
                >
                  <View className="flex-row items-center gap-3">
                    <CheckCircle size={24} color={colors.success} />
                    <View className="flex-1">
                      <Text style={{ color: colors.success }} className="font-semibold" numberOfLines={1}>
                        Analysing your deck
                      </Text>
                      <Text style={{ color: colors.text.secondary }} className="text-sm" numberOfLines={2}>
                        It will appear in your pipeline and finish on its own — you can close this and carry on.
                      </Text>
                    </View>
                  </View>
                </View>
              )}

              {/* Still processing — the server is finishing without us */}
              {isStillProcessing && (
                <View
                  className="rounded-xl p-4 mb-6"
                  style={{ backgroundColor: colors.primary[50], borderWidth: 1, borderColor: colors.primary[300] }}
                >
                  <View className="flex-row items-start gap-3">
                    <Clock size={20} color={colors.primary[600]} style={{ marginTop: 2 }} />
                    <View className="flex-1">
                      <Text style={{ color: colors.primary[700] }} className="font-semibold mb-1">
                        Still analysing
                      </Text>
                      <Text style={{ color: colors.text.secondary }} className="text-sm leading-5">
                        {analysisResult?.message ??
                          'This deck is taking longer than usual. It will appear in your pipeline automatically when it finishes — no need to upload it again.'}
                      </Text>
                    </View>
                  </View>
                </View>
              )}

              {/* Error Message */}
              {hasError && errorInfo && (
                <View
                  className="rounded-xl p-4 mb-6"
                  style={{ backgroundColor: colors.red.light, borderWidth: 1, borderColor: colors.error }}
                >
                  <View className="flex-row items-start gap-3">
                    <AlertTriangle size={20} color={colors.error} style={{ marginTop: 2 }} />
                    <View className="flex-1">
                      <Text style={{ color: colors.error }} className="font-semibold mb-1">
                        {errorInfo.title}
                      </Text>
                      <Text style={{ color: colors.text.secondary }} className="text-sm leading-5">
                        {errorInfo.message}
                      </Text>
                      {errorInfo.canRetry && (
                        <AnimatedPressable
                          onPress={() => {
                            resetAnalysis();
                            setAnalysisResult(null);
                          }}
                          accessibilityRole="button"
                          accessibilityLabel="Try analysis again"
                          className="flex-row items-center gap-2 mt-3 py-2 px-3 rounded-lg self-start"
                          style={{ backgroundColor: colors.surface.primary }}
                        >
                          <RotateCcw size={14} color={colors.primary[600]} />
                          <Text style={{ color: colors.primary[600] }} className="font-medium text-sm">
                            Try Again
                          </Text>
                        </AnimatedPressable>
                      )}
                    </View>
                  </View>
                </View>
              )}

              {/* Info Box */}
              {!isAnalyzing && !analysisResult?.success && !hasError && (
                <View
                  className="rounded-xl p-4 mb-6"
                  style={{ backgroundColor: colors.primary[50], borderWidth: 1, borderColor: colors.primary[100] }}
                >
                  <Text style={{ color: colors.primary[700] }} className="text-sm font-medium mb-2">
                    What happens next?
                  </Text>
                  <Text style={{ color: colors.primary[600] }} className="text-sm leading-5">
                    Our AI will analyze the pitch deck and generate a comprehensive Inverio report including
                    team assessment, market analysis, product evaluation, and risk factors.
                  </Text>
                </View>
              )}

              {/* Cancelled Message */}
              {stage === 'cancelled' && (
                <View
                  className="rounded-xl p-4 mb-6"
                  style={{ backgroundColor: colors.surface.secondary, borderWidth: 1, borderColor: colors.border.medium }}
                >
                  <View className="flex-row items-center gap-3">
                    <X size={20} color={colors.text.tertiary} />
                    <View className="flex-1">
                      <Text style={{ color: colors.text.primary }} className="font-semibold">
                        Analysis cancelled
                      </Text>
                      <Text style={{ color: colors.text.secondary }} className="text-sm">
                        You can restart the analysis or upload a different file.
                      </Text>
                    </View>
                  </View>
                </View>
              )}

    </AppModal>
  );
}
